/**
 * 🎯 OperationsCenterPage — Mesa de Controle
 *
 * Decision-first operational cockpit:
 *   Dominant Status Block: global status + action buttons
 *   Panel 1: Critical Problems (col-span-2 when critical)
 *   Panel 2: Bottlenecks
 *   Panel 3: Overdue Actions
 *   Panel 4: Active Workflows
 *   Panel 5: Team Workload
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { useAppToast } from '../components/AppToast';
import type { OpsTimeRange, OpsSeverityFilter } from '../components/OpsCenterRibbon';
import { useOperationsCenterRibbon } from '../hooks/useOperationsCenterRibbon';
import { desktopRibbonInputClass, desktopRibbonSelectClass } from '../components/ribbon/DesktopRibbonControls';
import { useExecutionSelection } from '../contexts/ExecutionSelectionContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { OperationsCenterService } from '../services/operationsCenterService';
import type {
  OpsDashboardMetrics, OpsActiveWorkflow, OpsBottleneck,
  OpsCriticalProblem, OpsOverdueAction, OpsTeamMember, OpsAlert,
} from '../services/operationsCenterService';
import {
  Activity, AlertTriangle, ArrowRight, ChevronRight, Clock, Users, Target,
  Workflow, BarChart3, Shuffle, Zap, Search, ChevronDown, Check, Filter, X,
  Gauge, TrendingUp, BellDot,
} from 'lucide-react';
import { useAlertEngine } from '../hooks/useAlertEngine';
import { BenchmarkTable } from '../components/BenchmarkTable';
import { OperationalHeatmap } from '../components/OperationalHeatmap';
import { calcularScoreOperacional } from '../services/heatmapService';
import type { CapacityConfig } from '../services/heatmapService';
import {
  runSimulation,
  SIM_ROLES,
  SIM_DEFAULT_FTE,
  SIM_SHIFT_MINUTES,
} from '../services/simulationEngine';

type OpsTab = 'live' | 'heatmap' | 'benchmark';

// ─── Styling Constants ──────────────────────────────────────

const PANEL_CLASS = 'rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] overflow-hidden text-[var(--text-primary)] shadow-[0_2px_8px_rgba(0,0,0,0.08)]';
const PANEL_HEADER_CLASS_BASE = 'flex items-center justify-between border-b border-[var(--border-default)] bg-[var(--bg-elevated)]';
const PANEL_TITLE_CLASS = 'text-[10px] font-semibold uppercase tracking-widest text-[var(--text-secondary)]';
const PANEL_BODY_CLASS = '';  // no overflow — panels expand naturally inside the unified scroll zone
const ROW_CLASS_BASE = 'flex items-center gap-3 border-b border-[var(--border-default)] text-[11px] text-[var(--text-primary)] last:border-b-0 hover:bg-[var(--button-hover)] transition-colors duration-200';
const BADGE_CLASS = 'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold border border-transparent';
const ACTION_LINK_CLASS = 'flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-blue-500 hover:text-blue-400 transition-colors cursor-pointer';
// density-aware class helpers (used where component has access to viewMode)
const rowClass = (compact: boolean) => `${ROW_CLASS_BASE} ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`;
const panelHeaderClass = (compact: boolean) => `${PANEL_HEADER_CLASS_BASE} ${compact ? 'px-3 py-2' : 'px-4 py-3'}`;

const LOAD_COLORS: Record<string, string> = {
  normal: 'bg-emerald-500',
  high: 'bg-amber-500',
  overload: 'bg-red-500',
};
const LOAD_LABELS: Record<string, string> = {
  normal: 'Normal',
  high: 'Alto',
  overload: 'Sobrecarga',
};
const LOAD_TEXT: Record<string, string> = {
  normal: 'text-emerald-600',
  high: 'text-amber-600',
  overload: 'text-red-600',
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-500',
  high: 'bg-orange-500/15 text-orange-500',
  medium: 'bg-amber-500/15 text-amber-500',
  low: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
};

// ─── Page ───────────────────────────────────────────────────

export const OperationsCenterPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();
  const { showToast, showActionToast, ToastContainer } = useAppToast();
  const navigate = useNavigate();
  const { selectInstance, selectTask, selectProblem } = useExecutionSelection();

  const [showAlerts, setShowAlerts] = useState(true);
  const [isWallboard, setIsWallboard] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'compact' | 'indicators'>('list');
  const [timeRange, setTimeRange] = useState<OpsTimeRange>('shift');
  const [severityFilter, setSeverityFilter] = useState<OpsSeverityFilter>('all');
  const [searchText, setSearchText] = useState('');
  const [liveNow, setLiveNow] = useState(Date.now());
  const [activeTab, setActiveTab] = useState<OpsTab>('live');

  // ── Basic heatmap capacity config (used when sim is OFF) ─────────────
  const [heatmapFte, setHeatmapFte] = useState<number>(3);
  const [heatmapShiftHours, setHeatmapShiftHours] = useState<number>(8);
  const [heatmapTasksPerPersonHour, setHeatmapTasksPerPersonHour] = useState<number>(1);
  const [heatmapGapMode, setHeatmapGapMode] = useState<boolean>(false);

  const heatmapCapacityConfig = useMemo((): CapacityConfig | undefined => {
    if (!heatmapGapMode) return undefined;
    return {
      totalCapacityPerHour: heatmapFte * heatmapTasksPerPersonHour,
      shiftHours: heatmapShiftHours,
      fte: heatmapFte,
      topRoleLabel: 'Equipe',
    };
  }, [heatmapGapMode, heatmapFte, heatmapShiftHours, heatmapTasksPerPersonHour]);

  // ── Simulation Engine state ────────────────────────────────────────────
  const [simMode, setSimMode] = useState(false);
  const [simFteByRole, setSimFteByRole] = useState<Record<string, number>>(
    () => Object.fromEntries(SIM_ROLES.map(r => [r, SIM_DEFAULT_FTE[r]])),
  );
  // timeFactor slider: -50 to +50 (added to 100 → multiplier = 1 + val/100)
  const [simTimeSlider, setSimTimeSlider] = useState(0);
  // demandFactor slider: 50 to 200 (multiplier = val/100)
  const [simDemandSlider, setSimDemandSlider] = useState(100);

  const resetSimulation = useCallback(() => {
    setSimFteByRole(Object.fromEntries(SIM_ROLES.map(r => [r, SIM_DEFAULT_FTE[r]])));
    setSimTimeSlider(0);
    setSimDemandSlider(100);
  }, []);

  // ── Live clock (1s tick for real-time elapsed) ────────────
  useEffect(() => {
    const id = window.setInterval(() => setLiveNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // ── Queries (auto-refresh every 60s) ──────────────────────

  const STALE = 15_000;
  const REFETCH = 15_000;

  const { data: metrics } = useQuery<OpsDashboardMetrics>({
    queryKey: ['ops-metrics', organizationId],
    queryFn: () => OperationsCenterService.getDashboardMetrics(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: workflows = [] } = useQuery<OpsActiveWorkflow[]>({
    queryKey: ['ops-workflows', organizationId],
    queryFn: () => OperationsCenterService.getActiveWorkflows(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: bottlenecks = [] } = useQuery<OpsBottleneck[]>({
    queryKey: ['ops-bottlenecks', organizationId],
    queryFn: () => OperationsCenterService.getBottlenecks(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: problems = [] } = useQuery<OpsCriticalProblem[]>({
    queryKey: ['ops-problems', organizationId],
    queryFn: () => OperationsCenterService.getCriticalProblems(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: overdueActions = [] } = useQuery<OpsOverdueAction[]>({
    queryKey: ['ops-actions', organizationId],
    queryFn: () => OperationsCenterService.getOverdueActions(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: teamWorkload = [] } = useQuery<OpsTeamMember[]>({
    queryKey: ['ops-team', organizationId],
    queryFn: () => OperationsCenterService.getTeamWorkload(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: alerts = [] } = useQuery<OpsAlert[]>({
    queryKey: ['ops-alerts', organizationId],
    queryFn: () => OperationsCenterService.getAlerts(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId && showAlerts,
  });

  // ── Handlers ──────────────────────────────────────────────

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ops-metrics'] });
    queryClient.invalidateQueries({ queryKey: ['ops-workflows'] });
    queryClient.invalidateQueries({ queryKey: ['ops-bottlenecks'] });
    queryClient.invalidateQueries({ queryKey: ['ops-problems'] });
    queryClient.invalidateQueries({ queryKey: ['ops-actions'] });
    queryClient.invalidateQueries({ queryKey: ['ops-team'] });
    queryClient.invalidateQueries({ queryKey: ['ops-alerts'] });
    showToast('info', 'Dados atualizados');
  }, [queryClient, showToast]);

  const criticalAlerts = useMemo(() => alerts.filter(a => a.severity === 'critical'), [alerts]);

  // ── Alert Engine ────────────────────────────────────────────
  const {
    alerts: engineAlerts,
    groups: alertGroups,
    topCritical: engineTopCritical,
    dismissAlert,
    resolveAlert,
    onAlertClick,
  } = useAlertEngine({
    workflows,
    bottlenecks,
    problems,
    overdueActions,
    silentMode: activeTab !== 'live',
  });

  // ── Filtered data (driven by severityFilter ribbon) ───────
  const filteredWorkflows = useMemo(() => {
    if (severityFilter === 'critical') return workflows.filter(w => w.is_overdue || (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 0));
    if (severityFilter === 'warning') return workflows.filter(w => w.is_overdue || (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 30));
    return workflows;
  }, [workflows, severityFilter]);

  const filteredProblems = useMemo(() => {
    if (severityFilter === 'critical') return problems.filter(p => p.severity === 'critical');
    if (severityFilter === 'warning') return problems.filter(p => p.severity === 'critical' || p.severity === 'high');
    return problems;
  }, [problems, severityFilter]);

  const filteredBottlenecks = useMemo(() => {
    if (severityFilter === 'critical') return bottlenecks.filter(bn => bn.stuck_count * (bn.avg_minutes || 1) > 200);
    return bottlenecks;
  }, [bottlenecks, severityFilter]);

  // ── Ribbon ────────────────────────────────────────────────

  // ── Ribbon filters (portal dropdown pattern) ─────────────

  type OpsDd = 'time' | 'severity' | null;
  const [openDd, setOpenDd] = useState<OpsDd>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const sevBtnRef = useRef<HTMLButtonElement>(null);
  const [ddPos, setDdPos] = useState({ top: 0, left: 0 });

  const toggleDd = useCallback((which: OpsDd) => {
    if (!which) return;
    const ref = which === 'time' ? timeBtnRef : sevBtnRef;
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setDdPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenDd(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openDd) return;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-ops-dd]')) return;
      setOpenDd(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDd]);

  const filterBtnClass = 'flex h-[28px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const ddItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const TIME_OPTIONS: { value: OpsTimeRange; label: string }[] = [
    { value: 'shift', label: 'Turno Atual' },
    { value: 'today', label: 'Hoje' },
    { value: 'last24h', label: 'Últimas 24h' },
  ];
  const SEVERITY_OPTIONS: { value: OpsSeverityFilter; label: string; color: string }[] = [
    { value: 'all', label: 'Todos', color: '#94a3b8' },
    { value: 'critical', label: 'Crítico', color: '#ef4444' },
    { value: 'warning', label: 'Alerta', color: '#f97316' },
    { value: 'info', label: 'Info', color: '#3b82f6' },
  ];

  const activeTimeLabel = TIME_OPTIONS.find(o => o.value === timeRange)?.label ?? 'Turno Atual';
  const activeSevOption = SEVERITY_OPTIONS.find(o => o.value === severityFilter);

  const opsFiltersWidget = useMemo(() => (
    <div className="flex items-center gap-1.5">
      {/* Período */}
      <button ref={timeBtnRef} data-ops-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('time')}>
        <Clock size={12} className="text-[var(--text-secondary)]" />
        <span>{activeTimeLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'time' && createPortal(
        <div data-ops-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
          {TIME_OPTIONS.map(o => (
            <button key={o.value} type="button" className={ddItemClass} onClick={() => { setTimeRange(o.value); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{o.label}</span>
              <Check size={11} className={timeRange === o.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Severidade */}
      <button ref={sevBtnRef} data-ops-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('severity')}>
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeSevOption?.label ?? 'Todos'}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'severity' && createPortal(
        <div data-ops-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
          {SEVERITY_OPTIONS.map(o => (
            <button key={o.value} type="button" className={ddItemClass} onClick={() => { setSeverityFilter(o.value); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: o.color }} />
              <span className="flex-1">{o.label}</span>
              <Check size={11} className={severityFilter === o.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex min-w-[140px] w-[200px] max-w-[220px] items-center">
        <div className="flex h-[28px] w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar alerta ou processo..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {searchText && (
            <button onClick={() => setSearchText('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [timeRange, severityFilter, searchText, openDd, ddPos, activeTimeLabel, activeSevOption, toggleDd]);

  const handleToggleAlerts = useCallback(() => {
    setShowAlerts(v => !v);
    setSeverityFilter(prev => prev === 'critical' ? 'all' : 'critical');
  }, []);

  const handleToggleWallboard = useCallback(() => {
    setIsWallboard(v => !v);
  }, []);

  const handleViewModeChange = useCallback((mode: 'list' | 'compact' | 'indicators') => {
    if (mode === 'compact') {
      setViewMode(prev => prev === 'compact' ? 'list' : 'compact');
      return;
    }
    setViewMode(mode);
  }, []);

  const ribbonConfig = useOperationsCenterRibbon({
    onRefresh,
    onToggleAlerts: handleToggleAlerts,
    showAlerts,
    alertCount: criticalAlerts.length,
    onToggleWallboard: handleToggleWallboard,
    isWallboard,
    viewMode,
    onViewModeChange: handleViewModeChange,
    afterGroups: opsFiltersWidget,
  });

  // ── Wallboard auto-refresh (15s) ─────────────────────
  useEffect(() => {
    if (!isWallboard) return;
    const id = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['ops-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['ops-workflows'] });
      queryClient.invalidateQueries({ queryKey: ['ops-bottlenecks'] });
      queryClient.invalidateQueries({ queryKey: ['ops-problems'] });
      queryClient.invalidateQueries({ queryKey: ['ops-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ops-team'] });
    }, 15_000);
    return () => window.clearInterval(id);
  }, [isWallboard, queryClient]);

  // ── Global status (decision block) ────────────────
  const globalStatus = useMemo(() => {
    const crits = metrics?.critical_problems ?? 0;
    const overdue = metrics?.overdue_tasks ?? 0;
    const bottleneckCount = bottlenecks.length;
    const overload = metrics?.team_overload ?? 0;

    if (crits > 0)
      return { level: 'critical' as const, text: `${crits} problema${crits > 1 ? 's' : ''} crítico${crits > 1 ? 's' : ''} — atenção imediata` };
    if (overdue > 5 || bottleneckCount > 3 || overload > 2)
      return { level: 'warning' as const, text: `${overdue} atrasos · ${bottleneckCount} gargalos · ${overload} sobrecargas` };
    return { level: 'stable' as const, text: 'Operação estável' };
  }, [metrics, bottlenecks.length]);

  const hasCritical = (metrics?.critical_problems ?? 0) > 0;
  const hasWarnings = (metrics?.overdue_tasks ?? 0) > 0 || bottlenecks.length > 0 || (metrics?.actions_overdue ?? 0) > 0;

  // ── Intelligence: next best action ─────────────────────
  const nextAction = useMemo(() => {
    const topProblem = problems.find(p => p.severity === 'critical') ?? problems[0];
    if (topProblem) {
      return {
        label: topProblem.title,
        reason: topProblem.severity === 'critical' ? 'Problema crítico requer atenção' : 'Problema prioritário aberto',
        impactoEstimado: topProblem.severity === 'critical' ? 'Alto impacto operacional' : 'Impacto moderado na equipe',
        path: '/problemas',
        actionLabel: 'Ver problema',
        severity: 'critical' as const,
      };
    }
    const topOverdue = overdueActions.sort((a, b) => b.days_overdue - a.days_overdue)[0];
    if (topOverdue) {
      return {
        label: topOverdue.title,
        reason: `${topOverdue.days_overdue}d em atraso`,
        impactoEstimado: `Estimativa: +${topOverdue.days_overdue}d de atraso acumulado`,
        path: '/acoes',
        actionLabel: 'Ver ação',
        severity: 'warning' as const,
      };
    }
    return null;
  }, [problems, overdueActions]);

  // ── Intelligence: top execution to resume ──────────────
  const topExecution = useMemo(() => {
    const overdue = workflows.filter(w => w.is_overdue).sort((a, b) => (b.elapsed_minutes ?? 0) - (a.elapsed_minutes ?? 0));
    return overdue[0] ?? workflows[0] ?? null;
  }, [workflows]);

  // ── Intelligence: overloaded team member ───────────────
  const overloadedMember = useMemo(() => {
    return teamWorkload.find(m => m.load_level === 'overload') ?? null;
  }, [teamWorkload]);
  const hasAnyOverload = overloadedMember !== null;

  // ── Benchmark date range (derived from timeRange) ────────────
  const { benchmarkStartDate, benchmarkEndDate } = useMemo(() => {
    const now = new Date();
    const end = now.toISOString();
    let start: Date;
    if (timeRange === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (timeRange === 'last24h') {
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    } else {
      // 'shift' — rolling 30 days for a meaningful benchmark sample
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    return { benchmarkStartDate: start.toISOString(), benchmarkEndDate: end };
  }, [timeRange]);

  // ── Operational Score (composite metric) ─────────────────
  const operationalScore = useMemo(() => calcularScoreOperacional({
    slaRiskPercent: metrics?.sla_risk_percent ?? 0,
    bottleneckCount: bottlenecks.length,
    overloadCount: metrics?.team_overload ?? 0,
    criticalProblems: metrics?.critical_problems ?? 0,
  }), [metrics, bottlenecks.length]);

  // ── Main loss insight ────────────────────────────────────
  const mainLoss = useMemo((): string | null => {
    if ((metrics?.critical_problems ?? 0) > 0) return `${metrics!.critical_problems} problema(s) crítico(s) aberto(s)`;
    if ((metrics?.sla_risk_percent ?? 0) > 15) return `SLA em risco em ${(metrics!.sla_risk_percent).toFixed(0)}% das execuções`;
    if (bottlenecks.length > 2) return `${bottlenecks.length} gargalos acumulando filas`;
    if ((metrics?.team_overload ?? 0) > 0) return `${metrics!.team_overload} membro(s) em sobrecarga`;
    if ((metrics?.overdue_tasks ?? 0) > 3) return `${metrics!.overdue_tasks} tarefas atrasadas`;
    return null;
  }, [metrics, bottlenecks.length]);

  // ── Efficiency rate from metrics ──────────────────────────
  const efficiencyRate = useMemo(() => {
    const total = metrics?.active_workflows ?? 0;
    const overdue = metrics?.overdue_tasks ?? 0;
    if (total === 0) return null;
    return Math.max(0, Math.round(((total - overdue) / total) * 100));
  }, [metrics]);

  // ── SLA compliance ────────────────────────────────────────
  const slaCompliance = useMemo(() => {
    const risk = metrics?.sla_risk_percent ?? 0;
    return Math.max(0, Math.round(100 - risk));
  }, [metrics]);

  // ── Priority aggregator ───────────────────────────────────────────────
  const globalPriorities = useMemo(() => {
    const bottleneckStages = new Set(bottlenecks.map(b => b.stage_name));
    const scored = workflows.map(w => {
      const slaScore = w.is_overdue
        ? 100
        : w.sla_remaining_minutes != null
          ? Math.max(0, Math.min(100, (120 - w.sla_remaining_minutes) / 120 * 100))
          : 0;
      const queueScore = Math.min(100, (w.waiting_minutes ?? 0) / 60 * 100);
      const bottleneckScore = w.current_stage_name && bottleneckStages.has(w.current_stage_name) ? 100 : 0;
      const criticalScore = w.is_overdue ? 100 : 0;
      const _score = Math.min(100, slaScore * 0.4 + queueScore * 0.3 + bottleneckScore * 0.2 + criticalScore * 0.1);
      return { ...w, _score };
    }).sort((a, b) => b._score - a._score);
    return {
      criticalTasks: scored.filter(t => t._score >= 80).slice(0, 5),
      riskyTasks: scored.filter(t => t._score >= 50 && t._score < 80).slice(0, 5),
      topFive: scored.slice(0, 5),
    };
  }, [workflows, bottlenecks]);

  // ── Simulation result (pure calculation, no network) ─────────────────
  const simResult = useMemo(() => {
    if (!simMode) return null;
    return runSimulation(
      {
        fteByRole: simFteByRole,
        timeFactor: 1 + simTimeSlider / 100,
        demandFactor: simDemandSlider / 100,
        shiftMinutes: SIM_SHIFT_MINUTES,
      },
      {
        efficiencyRate: efficiencyRate ?? 70,
        slaCompliance,
        bottleneckCount: bottlenecks.length,
        avgLeadTimeMinutes: metrics?.avg_lead_time_minutes ?? 120,
        totalActiveTasks: Math.max(1, metrics?.active_workflows ?? 10),
      },
    );
  }, [simMode, simFteByRole, simTimeSlider, simDemandSlider, efficiencyRate, slaCompliance, bottlenecks.length, metrics]);

  // ── Effective heatmap config (sim overrides basic when sim is ON) ─────
  const activeCapacityConfig = useMemo((): CapacityConfig | undefined => {
    if (simMode) {
      const totalFte = Object.values(simFteByRole).reduce((s, v) => s + v, 0);
      const demandMultiplier = simDemandSlider / 100;
      // Adjust effective capacity inversely to demand so gap colors reflect simulation
      return {
        totalCapacityPerHour: (totalFte / (SIM_SHIFT_MINUTES / 60)) / demandMultiplier,
        shiftHours: SIM_SHIFT_MINUTES / 60,
        fte: totalFte,
        topRoleLabel: 'Simulação',
      };
    }
    return heatmapCapacityConfig;
  }, [simMode, simFteByRole, simDemandSlider, heatmapCapacityConfig]);

  // ── Render ────────────────────────────────────────────────────

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
      <div className={`flex h-full w-full flex-col overflow-hidden bg-[var(--bg-app)] ${isWallboard ? 'p-2' : ''}`}>

        {!isWallboard && (
          <>
          {/* ── KPI Strip ──────────────────────────────────── */}
          <div className="shrink-0 mx-5 pt-3 pb-2">
            <div className="grid grid-cols-4 gap-2">
              {/* Score Operacional */}
              <div className={`flex flex-col gap-1 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5 ${
                operationalScore.level === 'excellent' ? 'border-l-[3px] border-l-emerald-500'
                : operationalScore.level === 'good'    ? 'border-l-[3px] border-l-sky-500'
                : operationalScore.level === 'warning' ? 'border-l-[3px] border-l-amber-500'
                : 'border-l-[3px] border-l-red-500'
              }`}>
                <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">Score Operacional</span>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[22px] font-black leading-none tabular-nums ${
                    operationalScore.level === 'excellent' ? 'text-emerald-400'
                    : operationalScore.level === 'good'    ? 'text-sky-400'
                    : operationalScore.level === 'warning' ? 'text-amber-400'
                    : 'text-red-400'
                  }`}>{operationalScore.score}</span>
                  <span className="text-[10px] font-medium text-[var(--text-muted)]">/ 100</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-[var(--bg-app)]">
                    <div className={`h-full rounded-full transition-all duration-500 ${
                      operationalScore.level === 'excellent' ? 'bg-emerald-400'
                      : operationalScore.level === 'good'    ? 'bg-sky-400'
                      : operationalScore.level === 'warning' ? 'bg-amber-400'
                      : 'bg-red-400'
                    }`} style={{ width: `${operationalScore.score}%` }} />
                  </div>
                  <span className={`text-[9px] font-bold ${
                    operationalScore.level === 'excellent' ? 'text-emerald-400'
                    : operationalScore.level === 'good'    ? 'text-sky-400'
                    : operationalScore.level === 'warning' ? 'text-amber-400'
                    : 'text-red-400'
                  }`}>{operationalScore.label}</span>
                </div>
                {mainLoss && (
                  <div className="mt-1 text-[9px] leading-tight text-[var(--text-muted)]">
                    Perda principal: <span className="font-medium text-[var(--text-secondary)]">{mainLoss}</span>
                  </div>
                )}
              </div>

              {/* Eficiência */}
              <div className={`flex flex-col gap-1 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5 transition-colors ${simMode ? 'border-l-[3px] border-l-violet-500' : ''}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">Eficiência</span>
                  {simResult && <span className={`text-[9px] font-bold tabular-nums ${simResult.deltas.efficiency >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    Δ {simResult.deltas.efficiency >= 0 ? '+' : ''}{simResult.deltas.efficiency}pp
                  </span>}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[22px] font-black leading-none tabular-nums ${
                    efficiencyRate === null ? 'text-[var(--text-secondary)]'
                    : efficiencyRate >= 85  ? 'text-emerald-400'
                    : efficiencyRate >= 65  ? 'text-amber-400'
                    : 'text-red-400'
                  }`}>
                    {efficiencyRate !== null ? `${efficiencyRate}%` : '—'}
                  </span>
                  {simResult && (
                    <span className={`text-[14px] font-bold tabular-nums ${simResult.simulatedEfficiency >= 85 ? 'text-emerald-400' : simResult.simulatedEfficiency >= 65 ? 'text-amber-400' : 'text-red-400'}`}>
                      → {simResult.simulatedEfficiency}%
                    </span>
                  )}
                </div>
                <span className="text-[9px] text-[var(--text-muted)]">
                  {metrics?.active_workflows ?? 0} exec. ativas
                </span>
              </div>

              {/* SLA Compliance */}
              <div className={`flex flex-col gap-1 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5 transition-colors ${simMode ? 'border-l-[3px] border-l-violet-500' : ''}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">SLA Compliance</span>
                  {simResult && <span className={`text-[9px] font-bold tabular-nums ${simResult.deltas.slaCompliance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    Δ {simResult.deltas.slaCompliance >= 0 ? '+' : ''}{simResult.deltas.slaCompliance}pp
                  </span>}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[22px] font-black leading-none tabular-nums ${
                    slaCompliance >= 95 ? 'text-emerald-400'
                    : slaCompliance >= 80 ? 'text-amber-400'
                    : 'text-red-400'
                  }`}>{slaCompliance}%</span>
                  {simResult && (
                    <span className={`text-[14px] font-bold tabular-nums ${simResult.simulatedSlaCompliance >= 95 ? 'text-emerald-400' : simResult.simulatedSlaCompliance >= 80 ? 'text-amber-400' : 'text-red-400'}`}>
                      → {simResult.simulatedSlaCompliance}%
                    </span>
                  )}
                </div>
                <span className="text-[9px] text-[var(--text-muted)]">
                  {(metrics?.sla_risk_percent ?? 0).toFixed(1)}% em risco
                </span>
              </div>

              {/* Gargalos */}
              <div className={`flex flex-col gap-1 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5 transition-colors ${simMode ? 'border-l-[3px] border-l-violet-500' : ''}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">Gargalos Ativos</span>
                  {simResult && simResult.deltas.bottlenecks !== 0 && (
                    <span className={`text-[9px] font-bold tabular-nums ${simResult.deltas.bottlenecks <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      Δ {simResult.deltas.bottlenecks > 0 ? '+' : ''}{simResult.deltas.bottlenecks}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[22px] font-black leading-none tabular-nums ${
                    bottlenecks.length === 0 ? 'text-emerald-400'
                    : bottlenecks.length <= 2 ? 'text-amber-400'
                    : 'text-red-400'
                  }`}>{bottlenecks.length}</span>
                  {simResult && (
                    <span className={`text-[14px] font-bold tabular-nums ${simResult.simulatedBottlenecks === 0 ? 'text-emerald-400' : simResult.simulatedBottlenecks <= 2 ? 'text-amber-400' : 'text-red-400'}`}>
                      → {simResult.simulatedBottlenecks}
                    </span>
                  )}
                </div>
                <span className="text-[9px] text-[var(--text-muted)]">
                  WIP {metrics?.current_wip ?? 0} · {metrics?.overdue_tasks ?? 0} atrasadas
                </span>
              </div>
            </div>
          </div>

          {/* ── Simulation Panel ───────────────────────── */}
          {simMode && (
            <div className="shrink-0 mx-5 mb-2 rounded-[4px] border border-violet-500/30 bg-[var(--bg-surface)] px-4 py-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gauge size={12} className="text-violet-400" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-violet-400">Modo Simulação</span>
                  <span className="rounded-[3px] bg-violet-500/20 px-1.5 py-0.5 text-[8px] font-bold uppercase text-violet-300">
                    Temporário · sem persistência
                  </span>
                </div>
                <button
                  type="button"
                  onClick={resetSimulation}
                  className="text-[10px] font-medium text-[var(--text-muted)] hover:text-violet-400 transition-colors"
                >
                  Resetar simulação
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* FTE por cargo */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">FTE por Cargo</span>
                  <div className="space-y-2">
                    {SIM_ROLES.map(role => (
                      <label key={role} className="flex items-center gap-2">
                        <span className="w-20 shrink-0 text-[10px] text-[var(--text-secondary)]">{role}</span>
                        <input
                          type="range" min={0.5} max={5} step={0.5}
                          value={simFteByRole[role] ?? 1}
                          onChange={e => setSimFteByRole(prev => ({ ...prev, [role]: Number(e.target.value) }))}
                          className="flex-1 accent-blue-500"
                        />
                        <span className="w-8 shrink-0 text-right text-[10px] font-semibold tabular-nums text-blue-400">
                          {(simFteByRole[role] ?? 1).toFixed(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Tempo de atividade */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Tempo de Atividade</span>
                  <label className="flex flex-col gap-1">
                    <input
                      type="range" min={-50} max={50} step={5}
                      value={simTimeSlider}
                      onChange={e => setSimTimeSlider(Number(e.target.value))}
                      className="accent-blue-500"
                    />
                    <div className="flex justify-between text-[9px] text-[var(--text-muted)]">
                      <span>−50%</span>
                      <span className={`font-semibold ${simTimeSlider > 0 ? 'text-amber-400' : simTimeSlider < 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                        {simTimeSlider > 0 ? `+${simTimeSlider}%` : simTimeSlider < 0 ? `${simTimeSlider}%` : 'Neutro'}
                      </span>
                      <span>+50%</span>
                    </div>
                  </label>
                  {simResult && (
                    <p className="mt-1.5 text-[9px] text-[var(--text-muted)]">
                      Lead time simulado: <span className="font-semibold text-violet-300">{simResult.simulatedLeadTime}m</span>
                    </p>
                  )}
                </div>

                {/* Demanda */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Demanda</span>
                  <label className="flex flex-col gap-1">
                    <input
                      type="range" min={50} max={200} step={10}
                      value={simDemandSlider}
                      onChange={e => setSimDemandSlider(Number(e.target.value))}
                      className="accent-blue-500"
                    />
                    <div className="flex justify-between text-[9px] text-[var(--text-muted)]">
                      <span>50%</span>
                      <span className={`font-semibold ${simDemandSlider > 100 ? 'text-amber-400' : simDemandSlider < 100 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                        {simDemandSlider}%
                      </span>
                      <span>200%</span>
                    </div>
                  </label>
                  {simResult && simResult.bottleneckRoles.length > 0 && (
                    <p className="mt-1.5 text-[9px] font-semibold text-red-400">
                      Gargalos: {simResult.bottleneckRoles.join(', ')}
                    </p>
                  )}
                  {simResult && simResult.bottleneckRoles.length === 0 && (
                    <p className="mt-1.5 text-[9px] text-emerald-400">Sem gargalos simulados</p>
                  )}
                </div>
              </div>

              {/* Role utilization strip */}
              {simResult && (
                <div className="mt-3 flex gap-2 border-t border-violet-500/20 pt-3">
                  {simResult.byRole.map(r => (
                    <div key={r.role} className="flex-1 min-w-0">
                      <div className="mb-1 flex items-center justify-between gap-1">
                        <span className="truncate text-[9px] text-[var(--text-muted)]">{r.role}</span>
                        <span className={`text-[9px] font-semibold tabular-nums ${
                          r.level === 'bottleneck' ? 'text-red-400' : r.level === 'attention' ? 'text-amber-400' : 'text-emerald-400'
                        }`}>{Math.round(r.utilization * 100)}%</span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-full bg-[var(--bg-app)]">
                        <div
                          className={`h-full rounded-full transition-all duration-200 ${
                            r.level === 'bottleneck' ? 'bg-red-500' : r.level === 'attention' ? 'bg-amber-400' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, Math.round(r.utilization * 100))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Tab Bar (hidden in indicators mode) ──────── */}
          {viewMode !== 'indicators' && (
          <div className="shrink-0 flex items-center justify-between border-b border-[var(--border-default)] mx-5">
            <div className="flex items-center gap-0">
              {([
                ['live',      'Ao Vivo',    Activity],
                ['heatmap',   'Heatmap',    BarChart3],
                ['benchmark', 'Benchmark',  TrendingUp],
              ] as [OpsTab, string, React.ElementType][]).map(([tab, label, Icon]) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide transition-colors border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'border-[var(--nav-active-bar)] text-[var(--text-primary)]'
                      : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                  }`}
                >
                  <Icon size={11} />
                  {label}
                </button>
              ))}
            </div>
            {/* Simulation toggle */}
            <button
              type="button"
              onClick={() => setSimMode(v => !v)}
              className={`mb-px flex items-center gap-1.5 rounded-[3px] border px-3 py-1 text-[10px] font-semibold uppercase tracking-wide transition-all ${
                simMode
                  ? 'border-violet-500/60 bg-violet-500/15 text-violet-400'
                  : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              <Gauge size={11} />
              {simMode ? 'Simulação ON' : 'Simulação OFF'}
            </button>
          </div>
          )} {/* end tab bar (hidden in indicators mode) */}
          </>
        )}

        {/* ── Scrollable body — everything below the tab bar ── */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-2 pb-8">

        {/* ── Status da Operação (bloco decisório) ──────── */}
        <div className={`mx-5 mb-2 space-y-1.5 ${activeTab !== 'live' ? 'hidden' : ''}`}>

          {/* Alert bar — only when critical or warning */}
          {globalStatus.level !== 'stable' && (
            <div className={`flex items-start gap-3 rounded-[4px] border px-4 py-2.5 ${
              globalStatus.level === 'critical'
                ? 'border-red-500/40 bg-red-500/6'
                : 'border-amber-500/40 bg-amber-500/6'
            }`}>
              <span className="text-[12px] shrink-0 mt-0.5">{globalStatus.level === 'critical' ? '🔴' : '🟡'}</span>
              <div className="flex-1 min-w-0">
                <span className={`text-[11px] font-semibold ${
                  globalStatus.level === 'critical' ? 'text-red-500' : 'text-amber-500'
                }`}>
                  {engineTopCritical?.titulo ?? globalStatus.text}
                </span>
                {(() => {
                  // Prefer engine top critical for richer context
                  if (engineTopCritical) {
                    return (
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[var(--text-secondary)]">
                        <span>{engineTopCritical.impacto}</span>
                        {engineTopCritical.stage_name && (
                          <><span className="text-[var(--border-default)]">·</span><span>Etapa: {engineTopCritical.stage_name}</span></>
                        )}
                      </div>
                    );
                  }
                  const topP = problems.find(p => p.severity === 'critical') ?? problems[0];
                  if (!topP) return null;
                  return (
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[var(--text-secondary)]">
                      <span>⏱ {formatTimeOpen(topP.created_at)} aberto</span>
                      <span className="text-[var(--border-default)]">·</span>
                      <span>Impacto: <span className={topP.severity === 'critical' ? 'text-red-500 font-medium' : 'text-amber-500 font-medium'}>{topP.severity === 'critical' ? 'Alto' : 'Médio'}</span></span>
                      {topP.department && <><span className="text-[var(--border-default)]">·</span><span>Área: {topP.department}</span></>}
                    </div>
                  );
                })()}
              </div>
              <div className="flex shrink-0 items-center gap-1.5 mt-0.5">
                <button
                  type="button"
                  onClick={() => {
                    if (engineTopCritical?.execution_id) {
                      onAlertClick(engineTopCritical);
                      navigate(`/minha-mesa?task_id=${encodeURIComponent(engineTopCritical.execution_id)}`);
                    } else {
                      navigate(globalStatus.level === 'critical' ? '/problemas' : '/acoes');
                    }
                  }}
                  className={`flex items-center gap-1 rounded-[3px] px-2.5 py-1 text-[10px] font-semibold text-white active:scale-95 transition-all ${
                    globalStatus.level === 'critical' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  Resolver agora
                </button>
                <button
                  type="button"
                  onClick={() => navigate(globalStatus.level === 'critical' ? '/problemas' : '/operacoes')}
                  className="flex items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 py-1 text-[10px] font-medium text-[var(--text-primary)] hover:bg-[var(--button-hover)] active:scale-95 transition-all"
                >
                  Ver detalhes
                </button>
                {globalStatus.level === 'critical' && (
                  <button
                    type="button"
                    onClick={() => navigate('/equipe')}
                    className="flex items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 py-1 text-[10px] font-medium text-[var(--text-primary)] hover:bg-[var(--button-hover)] active:scale-95 transition-all"
                  >
                    Delegar
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Alert groups strip — when engine has active grouped alerts */}
          {alertGroups.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <BellDot size={11} className="text-[var(--text-muted)] shrink-0" />
              {alertGroups.slice(0, 4).map(g => (
                <div
                  key={g.key}
                  className={`inline-flex items-center gap-1.5 rounded-[3px] border px-2 py-0.5 text-[10px] font-medium cursor-pointer hover:opacity-90 transition-opacity ${
                    g.tipo === 'sla' || g.tipo === 'critico'
                      ? 'border-red-500/30 bg-red-500/8 text-red-500'
                      : g.tipo === 'preditivo'
                      ? 'border-indigo-400/30 bg-indigo-400/8 text-indigo-400'
                      : g.tipo === 'gargalo'
                      ? 'border-amber-500/30 bg-amber-500/8 text-amber-600'
                      : 'border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onAlertClick(g.topAlert);
                      if (g.topAlert.execution_id) navigate(`/minha-mesa?task_id=${encodeURIComponent(g.topAlert.execution_id)}`);
                      else navigate('/operacoes');
                    }}
                    className="inline-flex min-w-0 items-center gap-1.5 text-left"
                  >
                    {g.count > 1 && <span className="font-bold">{g.count}×</span>}
                    <span className="truncate max-w-[180px]">{g.titulo}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissAlert(g.topAlert.id)}
                    className="hover:opacity-70 transition-opacity ml-0.5"
                    aria-label="Ignorar alerta"
                  >
                    <X size={9} />
                  </button>
                </div>
              ))}
              {alertGroups.length > 4 && (
                <span className="text-[10px] text-[var(--text-muted)]">+{alertGroups.length - 4} mais</span>
              )}
            </div>
          )}

          {/* Ação Recomendada — CTA block */}
          {nextAction && (
            <div className="rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1.5">Ação Recomendada</div>
              <div className="text-[11px] font-semibold text-[var(--text-primary)] truncate mb-0.5">
                {nextAction.path.startsWith('/execucoes') && topExecution?.current_stage_name
                  ? `Resolver atraso na etapa "${topExecution.current_stage_name}"`
                  : nextAction.label}
              </div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-[10px] text-[var(--text-secondary)]">{nextAction.reason}</span>
                {(() => {
                  const execCtx = nextAction.path.startsWith('/execucoes') ? topExecution : null;
                  if (!execCtx) return null;
                  return (
                    <>
                      {execCtx.current_stage_name && (
                        <span className="text-[10px] text-[var(--text-secondary)]">
                          Etapa: <span className="font-medium text-[var(--text-primary)]">{execCtx.current_stage_name}</span>
                        </span>
                      )}
                      {execCtx.assigned_to_users && execCtx.assigned_to_users.length > 0 && (
                        <span className="text-[10px] text-[var(--text-secondary)]">
                          Resp.: <span className="font-medium text-[var(--text-primary)]">{execCtx.assigned_to_users[0]}</span>
                        </span>
                      )}
                      {execCtx.sla_remaining_minutes != null && (
                        <span className={`text-[10px] font-semibold ${
                          execCtx.sla_remaining_minutes <= 0 ? 'text-red-500'
                          : execCtx.sla_remaining_minutes <= 30 ? 'text-amber-500'
                          : 'text-emerald-500'
                        }`}>
                          SLA: {execCtx.sla_remaining_minutes <= 0
                            ? `estourado há ${formatDuration(Math.abs(execCtx.sla_remaining_minutes))}`
                            : `${formatDuration(execCtx.sla_remaining_minutes)} rest.`}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    if (engineTopCritical?.execution_id) navigate(`/minha-mesa?task_id=${encodeURIComponent(engineTopCritical.execution_id)}`);
                    else navigate(nextAction.path);
                  }}
                  className={`flex items-center gap-1 rounded-[3px] px-2.5 py-1 text-[10px] font-semibold text-white active:scale-95 transition-all ${
                    engineTopCritical ? 'bg-red-500 hover:bg-red-600' :
                    nextAction.severity === 'critical' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  {engineTopCritical ? 'Abrir tarefa crítica' :
                    nextAction.path.startsWith('/execucoes') ? 'Abrir execução' : 'Resolver agora'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (nextAction.path === '/problemas' && problems[0]) navigate(`/problemas?id=${encodeURIComponent(problems[0].id)}`);
                    else navigate(nextAction.path);
                  }}
                  className="flex items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2.5 py-1 text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--button-hover)] active:scale-95 transition-all"
                >
                  Ver causa
                </button>
              </div>
            </div>
          )}

        </div>

        {/* ── PRIORIDADES OPERACIONAIS (only when urgency exists) ── */}
        {activeTab === 'live' && (() => {
          const stalledWfs = workflows.filter(w => w.is_overdue || w.status === 'on_hold');
          const waitAboveLimit = (metrics?.waiting_tasks ?? 0) > 5;
          const slaAtRisk = (metrics?.sla_risk_percent ?? 0) > 5;
          const showPriorities = stalledWfs.length > 0 || waitAboveLimit || slaAtRisk;
          if (!showPriorities) return null;
          return (
            <div className="mx-5 mb-2">
              <div className={`${PANEL_CLASS} border-amber-500/40`}>
                <div className={panelHeaderClass(viewMode === 'compact')}>
                  <div className="flex items-center gap-1.5">
                    <Zap size={12} className="text-amber-500" />
                    <span className={PANEL_TITLE_CLASS}>Prioridades Operacionais</span>
                    <span className="text-[10px] font-semibold text-amber-500 animate-pulse">
                      {stalledWfs.length + (waitAboveLimit ? 1 : 0) + (slaAtRisk ? 1 : 0)}
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-[var(--border-default)]">
                  {stalledWfs.slice(0, 5).map(wf => (
                    <button
                      key={wf.id}
                      type="button"
                      onClick={() => { selectInstance(wf.id); navigate(`/execucoes/instancias/${encodeURIComponent(wf.id)}`); }}
                      className={`${rowClass(viewMode === "compact")} w-full text-left cursor-pointer`}
                    >
                      <span className="text-red-500 text-[10px]">⚠</span>
                      <span className="flex-1 truncate font-medium">{wf.title}</span>
                      {wf.current_stage_name && (
                        <span className="text-[10px] text-slate-400">Etapa: {wf.current_stage_name}</span>
                      )}
                      {wf.waiting_minutes != null && wf.waiting_minutes > 0 && (
                        <span className="text-[10px] text-amber-500">Fila: {formatDuration(wf.waiting_minutes)}</span>
                      )}
                      {wf.is_overdue && <span className="text-[9px] text-red-500 font-semibold">ATRASADO</span>}
                      <ChevronRight size={10} className="shrink-0 text-slate-400" />
                    </button>
                  ))}
                  {waitAboveLimit && (
                    <button
                      type="button"
                      onClick={() => navigate('/minha-mesa')}
                      className={`${rowClass(viewMode === "compact")} w-full text-left cursor-pointer`}
                    >
                      <span className="text-amber-500 text-[10px]">⚠</span>
                      <span className="flex-1 text-[11px] font-medium">{metrics?.waiting_tasks ?? 0} tarefas aguardando atribuição</span>
                      <ChevronRight size={10} className="shrink-0 text-slate-400" />
                    </button>
                  )}
                  {slaAtRisk && (
                    <button
                      type="button"
                      onClick={() => navigate('/operacoes')}
                      className={`${rowClass(viewMode === "compact")} w-full text-left cursor-pointer`}
                    >
                      <span className={(metrics?.sla_risk_percent ?? 0) > 15 ? 'text-red-500 text-[10px]' : 'text-amber-500 text-[10px]'}>
                        {(metrics?.sla_risk_percent ?? 0) > 15 ? '🔴' : '⚠'}
                      </span>
                      <span className="flex-1 text-[11px] font-medium">SLA em risco: {(metrics?.sla_risk_percent ?? 0).toFixed(1)}%</span>
                      <ChevronRight size={10} className="shrink-0 text-slate-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Panels Grid ────────────────────────────────── */}
        {activeTab === 'live' && viewMode !== 'indicators' && (
        <div className={`px-5 pb-4 ${isWallboard ? 'pt-0' : ''}`}>
          <div className="grid gap-2 grid-cols-2">

            {/* Panel A — Ações Atrasadas (only when has data — moved to top) */}
            {overdueActions.length > 0 && (
            <div className={`${PANEL_CLASS} col-span-2`}>
              <div className={panelHeaderClass(viewMode === 'compact')}>
                <div className="flex items-center gap-1.5">
                  <Target size={12} className="text-orange-500" />
                  <span className={PANEL_TITLE_CLASS}>Ações Atrasadas</span>
                  <span className="text-[10px] font-semibold text-orange-500">{overdueActions.length}</span>
                </div>
                <button onClick={() => navigate('/acoes')} className={ACTION_LINK_CLASS}>
                  Ver plano <ChevronRight size={10} />
                </button>
              </div>
              <div className={PANEL_BODY_CLASS}>
                {overdueActions.map(a => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => navigate('/acoes')}
                    className={`${rowClass(viewMode === 'compact')} w-full text-left cursor-pointer`}
                  >
                    <span className="flex-1 truncate">{a.title}</span>
                    <span className="text-[10px] text-red-500 font-semibold shrink-0">-{a.days_overdue}d</span>
                    {a.project_name && <span className="text-[10px] text-[var(--text-secondary)] truncate max-w-[80px]">{a.project_name}</span>}
                    {a.assigned_to && <span className="text-[10px] text-[var(--text-muted)] truncate max-w-[80px]">{a.assigned_to}</span>}
                    <ChevronRight size={10} className="shrink-0 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Panel B — Execuções em Risco (priority-scored top 5) */}
            {(() => {
              const atRisk = globalPriorities.criticalTasks.length > 0
                ? globalPriorities.criticalTasks
                : globalPriorities.riskyTasks;
              if (atRisk.length === 0) return null;
              return (
                <div className={`${PANEL_CLASS} col-span-2 border-red-500/20`}>
                  <div className={panelHeaderClass(viewMode === 'compact')}>
                    <div className="flex items-center gap-1.5">
                      <Zap size={12} className="text-red-500" />
                      <span className={PANEL_TITLE_CLASS}>Execuções em Risco</span>
                      <span className="text-[10px] font-semibold text-red-500 animate-pulse">{atRisk.length}</span>
                    </div>
                    <button onClick={() => navigate('/execucoes')} className={ACTION_LINK_CLASS}>
                      Abrir <ChevronRight size={10} />
                    </button>
                  </div>
                  <div className={PANEL_BODY_CLASS}>
                    {atRisk.map(wf => {
                      const score = wf._score;
                      const barColor = score >= 80 ? '#ef4444' : score >= 50 ? '#f59e0b' : '#22c55e';
                      const levelLabel = score >= 80 ? 'CRÍTICO' : 'RISCO';
                      const levelClass = score >= 80 ? 'text-red-500 bg-red-500/10' : 'text-amber-500 bg-amber-500/10';
                      return (
                        <button
                          key={wf.id}
                          type="button"
                          onClick={() => { selectInstance(wf.id); navigate(`/execucoes/instancias/${encodeURIComponent(wf.id)}`); }}
                          className={`relative w-full text-left cursor-pointer border-b border-[var(--border-default)] last:border-b-0 ${viewMode === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5'} hover:bg-[var(--button-hover)] transition-colors`}
                        >
                          {/* Priority bar */}
                          <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[4px]" style={{ backgroundColor: barColor }} />
                          <div className="flex items-center gap-2 pl-2">
                            <span className="text-[11px] font-semibold flex-1 truncate text-[var(--text-primary)]">{wf.title}</span>
                            <span className={`text-[9px] font-bold shrink-0 px-1.5 py-0.5 rounded-[3px] ${levelClass}`}>{levelLabel}</span>
                            <span className="text-[9px] font-mono font-bold tabular-nums text-[var(--text-muted)] shrink-0">{Math.round(score)}</span>
                            <ChevronRight size={10} className="shrink-0 text-slate-400" />
                          </div>
                          {viewMode !== 'compact' && (
                          <div className="flex items-center gap-3 mt-0.5 pl-4 text-[10px] text-[var(--text-secondary)]">
                            {wf.current_stage_name && <span>Etapa: <span className="font-medium text-[var(--text-primary)]">{wf.current_stage_name}</span></span>}
                            {wf.sla_remaining_minutes != null && (
                              <span className={`font-semibold ${wf.sla_remaining_minutes <= 0 ? 'text-red-500' : wf.sla_remaining_minutes <= 30 ? 'text-amber-500' : 'text-[var(--text-secondary)]'}`}>
                                {wf.sla_remaining_minutes <= 0
                                  ? `SLA estourado há ${formatDuration(Math.abs(wf.sla_remaining_minutes))}`
                                  : `SLA: ${formatDuration(wf.sla_remaining_minutes)} rest.`}
                              </span>
                            )}
                            {wf.waiting_minutes != null && wf.waiting_minutes > 0 && (
                              <span className="font-semibold text-red-400">Fila: {formatDuration(wf.waiting_minutes)}</span>
                            )}
                          </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Panel 1 — Gargalos */}
            <div className={PANEL_CLASS}>
              <div className={panelHeaderClass(viewMode === 'compact')}>
                <div className="flex items-center gap-1.5">
                  <BarChart3 size={12} className="text-amber-500" />
                  <span className={PANEL_TITLE_CLASS}>Gargalos</span>
                  {filteredBottlenecks.length > 0 && <span className="text-[10px] font-semibold text-amber-500">{filteredBottlenecks.length}</span>}
                </div>
                <button onClick={() => navigate('/operacoes')} className={ACTION_LINK_CLASS}>
                  Ver fluxos <ChevronRight size={10} />
                </button>
              </div>
              <div className={PANEL_BODY_CLASS}>
                {filteredBottlenecks.length === 0 ? (
                  <EmptyPanel label={severityFilter !== 'all' ? 'Sem gargalos no filtro ativo' : 'Nenhum gargalo identificado'} stable />
                ) : (
                  [...filteredBottlenecks]
                    .map(bn => ({ ...bn, impactScore: bn.stuck_count * (bn.avg_minutes || 1) }))
                    .sort((a, b) => b.impactScore - a.impactScore)
                    .map(bn => (
                    <button
                      key={bn.stage_name}
                      type="button"
                      onClick={() => navigate(`/execucoes?stage=${encodeURIComponent(bn.stage_name)}`)}
                      className={`w-full text-left cursor-pointer border-b border-[var(--border-default)] last:border-b-0 ${viewMode === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2'} hover:bg-[var(--button-hover)] transition-colors`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-[11px] font-medium text-[var(--text-primary)]">{bn.stage_name}</span>
                        <span className="text-[10px] font-semibold text-amber-500 shrink-0">{bn.stuck_count} tarefa{bn.stuck_count !== 1 ? 's' : ''}</span>
                        {bn.impactScore > 200 ? (
                          <span className="inline-flex items-center rounded-[3px] bg-red-500/15 px-1.5 py-0.5 text-[9px] font-bold text-red-500">↑ CRÍTICO</span>
                        ) : bn.impactScore > 60 ? (
                          <span className="inline-flex items-center rounded-[3px] bg-orange-400/15 px-1.5 py-0.5 text-[9px] font-bold text-orange-500">↑ RISCO</span>
                        ) : null}
                        <ChevronRight size={10} className="shrink-0 text-slate-400" />
                      </div>
                      {viewMode !== 'compact' && (
                      <div className="flex items-center gap-3 mt-0.5 pl-0 text-[10px] text-[var(--text-secondary)]">
                        <span>~{formatDuration(bn.avg_minutes)} médio</span>
                        {(() => {
                          // Estimate capacity: assume ~4 tasks/hour per stage as baseline
                          // occupancy = tasks stuck vs. sustainable throughput
                          const capacityEstimate = Math.max(1, Math.round(bn.avg_minutes > 0 ? 60 / bn.avg_minutes : 2));
                          const occupancy = Math.min(200, Math.round((bn.stuck_count / capacityEstimate) * 100));
                          const leadImpact = bn.avg_minutes * bn.stuck_count;
                          return (
                            <>
                              <span className={`font-semibold ${
                                occupancy >= 100 ? 'text-red-500' : occupancy >= 80 ? 'text-amber-500' : 'text-[var(--text-secondary)]'
                              }`}>{occupancy}% ocup. {occupancy >= 100 ? '🔴' : occupancy >= 80 ? '🟡' : ''}</span>
                              {leadImpact > 0 && (
                                <span>+{formatDuration(leadImpact)} lead time</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Panel 2 — Problemas (sorted by criticidade) */}
            <div className={PANEL_CLASS}>
              <div className={panelHeaderClass(viewMode === 'compact')}>
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={12} className="text-red-500" />
                  <span className={PANEL_TITLE_CLASS}>Problemas</span>
                  {filteredProblems.length > 0 && <span className="text-[10px] font-semibold text-red-500">{filteredProblems.length}</span>}
                </div>
                <button onClick={() => navigate('/problemas')} className={ACTION_LINK_CLASS}>
                  Ver todos <ChevronRight size={10} />
                </button>
              </div>
              <div className={PANEL_BODY_CLASS}>
                {filteredProblems.length === 0 ? (
                  <EmptyPanel label={severityFilter !== 'all' ? 'Sem problemas no filtro ativo' : 'Nenhum problema registrado'} stable />
                ) : (
                  [...filteredProblems]
                    .sort((a, b) => {
                      const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
                      return (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
                    })
                    .map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { selectProblem(p.id); navigate(`/problemas/${encodeURIComponent(p.id)}`); }}
                      className={`w-full text-left cursor-pointer border-b border-[var(--border-default)] last:border-b-0 ${viewMode === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5'} hover:bg-[var(--button-hover)] transition-colors group`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`${BADGE_CLASS} shrink-0 ${
                          p.severity === 'critical'
                            ? 'bg-red-600 text-white border-red-600'
                            : (SEVERITY_BADGE[p.severity] ?? SEVERITY_BADGE.low)
                        }`}>
                          {p.severity === 'critical' ? 'CRI' : 'ALT'}
                        </span>
                        <span className="flex-1 truncate text-[11px] font-medium text-[var(--text-primary)] group-hover:text-[var(--text-primary)]">{p.title}</span>
                        <span className="text-[10px] text-[var(--text-muted)] shrink-0">⏱ {formatTimeOpen(p.created_at)}</span>
                        {p.department && <span className="text-[10px] text-[var(--text-secondary)] truncate max-w-[70px]">{p.department}</span>}
                        <ChevronRight size={10} className="shrink-0 text-slate-400 group-hover:text-[var(--text-secondary)] transition-colors" />
                      </div>
                      {viewMode !== 'compact' && (p.severity === 'critical' || p.severity === 'high') && (
                        <div className="mt-0.5 pl-[calc(2rem+8px)] flex items-center gap-2 text-[10px]">
                          <span className={p.severity === 'critical' ? 'text-red-500 font-medium' : 'text-orange-500 font-medium'}>
                            Impacto: {p.severity === 'critical' ? 'Alto' : 'Médio'}
                          </span>
                          <span className="text-[var(--text-muted)]">·</span>
                          <span className="text-[var(--text-secondary)]">Aberto há {formatTimeOpen(p.created_at)}</span>
                          {p.department && (
                            <><span className="text-[var(--text-muted)]">·</span>
                            <span className="text-[var(--text-secondary)] truncate max-w-[80px]">{p.department}</span></>
                          )}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Panel 3 — Execuções Ativas (full width) */}
            <div className={`${PANEL_CLASS} col-span-2`}>
              <div className={panelHeaderClass(viewMode === 'compact')}>
                <div className="flex items-center gap-1.5">
                  <Workflow size={12} className="text-blue-500" />
                  <span className={PANEL_TITLE_CLASS}>Execuções Ativas</span>
                  {filteredWorkflows.length > 0 && <span className="text-[10px] font-semibold text-blue-500">{filteredWorkflows.length}</span>}
                </div>
                <button onClick={() => navigate('/execucoes')} className={ACTION_LINK_CLASS}>
                  Abrir execuções críticas <ChevronRight size={10} />
                </button>
              </div>
              <div className={PANEL_BODY_CLASS}>
                {filteredWorkflows.length === 0 ? (
                  <EmptyPanel label={severityFilter !== 'all' ? 'Sem execuções no filtro ativo' : 'Nenhuma execução em andamento'} cta={severityFilter === 'all' ? 'Iniciar execução' : undefined} onClick={severityFilter === 'all' ? () => navigate('/execucoes') : undefined} />
                ) : (
                  (() => {
                    const sorted = [...filteredWorkflows].sort((a, b) => {
                      // Sort by priority score first
                      const bottleneckStages = new Set(bottlenecks.map(bn => bn.stage_name));
                      const score = (w: typeof a) => {
                        const slaScore = w.is_overdue ? 100 : w.sla_remaining_minutes != null ? Math.max(0, Math.min(100, (120 - w.sla_remaining_minutes) / 120 * 100)) : 0;
                        const queueScore = Math.min(100, (w.waiting_minutes ?? 0) / 60 * 100);
                        const bottleneckScore = w.current_stage_name && bottleneckStages.has(w.current_stage_name) ? 100 : 0;
                        return slaScore * 0.4 + queueScore * 0.3 + bottleneckScore * 0.2 + (w.is_overdue ? 10 : 0);
                      };
                      return score(b) - score(a);
                    });
                    return sorted.map((wf, idx) => {
                      const bottleneckStages = new Set(bottlenecks.map(bn => bn.stage_name));
                      const slaScore = wf.is_overdue ? 100 : wf.sla_remaining_minutes != null ? Math.max(0, Math.min(100, (120 - wf.sla_remaining_minutes) / 120 * 100)) : 0;
                      const queueScore = Math.min(100, (wf.waiting_minutes ?? 0) / 60 * 100);
                      const bottleneckScore = wf.current_stage_name && bottleneckStages.has(wf.current_stage_name) ? 100 : 0;
                      const rowScore = Math.min(100, slaScore * 0.4 + queueScore * 0.3 + bottleneckScore * 0.2 + (wf.is_overdue ? 10 : 0));
                      const barColor = rowScore >= 80 ? '#ef4444' : rowScore >= 50 ? '#f59e0b' : '#22c55e';
                      const slaOverdueMin = wf.sla_remaining_minutes != null && wf.sla_remaining_minutes < 0 ? Math.abs(wf.sla_remaining_minutes) : null;
                      const slaLabel = wf.sla_remaining_minutes == null ? null
                        : wf.sla_remaining_minutes <= 0 ? <span className="text-[10px] font-semibold text-red-500">SLA estourado há {formatDuration(slaOverdueMin ?? 0)}</span>
                        : wf.sla_remaining_minutes <= 30 ? <span className="text-[10px] font-semibold text-amber-500">SLA: {formatDuration(wf.sla_remaining_minutes)} rest.</span>
                        : <span className="text-[10px] text-[var(--text-secondary)]">SLA: {formatDuration(wf.sla_remaining_minutes)} rest.</span>;
                      const elapsed = wf.started_at ? Math.round((liveNow - new Date(wf.started_at).getTime()) / 60000) : wf.elapsed_minutes;
                      const priorityLabel = rowScore >= 80
                        ? <span className="text-[9px] font-bold text-red-500 shrink-0 bg-red-500/10 px-1.5 py-0.5 rounded-[3px]">CRÍTICO</span>
                        : rowScore >= 50
                        ? <span className="text-[9px] font-bold text-amber-500 shrink-0 bg-amber-500/10 px-1.5 py-0.5 rounded-[3px]">RISCO</span>
                        : null;
                      return (
                        <button
                          key={wf.id}
                          type="button"
                          onClick={() => { selectInstance(wf.id); navigate(`/execucoes/instancias/${encodeURIComponent(wf.id)}`); }}
                          className={`relative w-full text-left cursor-pointer border-b border-[var(--border-default)] last:border-b-0 ${viewMode === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2'} hover:bg-[var(--button-hover)] transition-colors duration-150`}
                        >
                          {/* Priority bar */}
                          <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[4px]" style={{ backgroundColor: barColor }} />
                          <div className="flex items-center gap-2 pl-2">
                            <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                              wf.is_overdue ? 'bg-red-500 animate-pulse' :
                              wf.status === 'on_hold' || wf.task_status === 'paused' || wf.task_status === 'PAUSED' ? 'bg-amber-500' : 'bg-emerald-500'
                            }`} />
                            <span className="text-[11px] font-medium flex-1 truncate text-[var(--text-primary)]">{wf.title}</span>
                            {priorityLabel}
                            {!priorityLabel && wf.is_overdue && <span className="text-[9px] font-bold text-red-500 shrink-0">ATRASADO</span>}
                            {!wf.is_overdue && !!wf.pause_reason && (
                              <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-500 shrink-0">
                                <AlertTriangle size={8} /> {wf.pause_reason}
                              </span>
                            )}
                          </div>
                          {viewMode !== 'compact' && (
                          <div className="flex items-center gap-3 mt-0.5 pl-5">
                            {wf.current_stage_name && (
                              <span className="text-[10px] text-[var(--text-secondary)]">
                                Etapa: <span className="font-medium text-[var(--text-primary)]">{wf.current_stage_name}</span>
                              </span>
                            )}
                            {slaLabel}
                            {wf.waiting_minutes != null && wf.waiting_minutes > 0 && (
                              <span className="text-[10px] font-semibold" style={{ color: '#ef4444' }}>
                                Fila: {formatDuration(wf.waiting_minutes)}
                              </span>
                            )}
                            {elapsed != null && (
                              <span className="text-[10px] text-[var(--text-secondary)]">
                                {formatDuration(elapsed)} dec.
                              </span>
                            )}
                          </div>
                          )}
                        </button>
                      );
                    });
                  })()
                )}
              </div>
            </div>

            {/* Panel 4 — Minha Mesa / Carga (hidden when no data) */}
            {teamWorkload.length > 0 && (
            <div className={PANEL_CLASS}>
              <div className={panelHeaderClass(viewMode === 'compact')}>
                <div className="flex items-center gap-1.5">
                  <Users size={12} className={hasAnyOverload ? 'text-red-500' : 'text-blue-500'} />
                  <span className={PANEL_TITLE_CLASS}>Minha Mesa</span>
                  {hasAnyOverload && (
                    <span className="text-[9px] font-semibold text-red-500 animate-pulse">SOBRECARGA</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {hasAnyOverload && (
                    <button onClick={() => navigate('/equipe')} className="flex items-center gap-0.5 text-[10px] font-medium text-red-500 hover:text-red-700 transition-colors active:scale-95">
                      <Shuffle size={10} /> Redistribuir
                    </button>
                  )}
                  <button onClick={() => navigate('/minha-mesa')} className={ACTION_LINK_CLASS}>
                    Abrir <ChevronRight size={10} />
                  </button>
                </div>
              </div>
              <div className={PANEL_BODY_CLASS}>
                {teamWorkload.map(m => (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => navigate('/equipe')}
                    className={`w-full text-left cursor-pointer border-b border-[var(--border-default)] last:border-b-0 ${viewMode === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5'} hover:bg-[var(--button-hover)] transition-colors ${
                      m.load_level === 'overload' ? 'bg-red-500/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full shrink-0 ${
                        LOAD_COLORS[m.load_level]
                      } ${m.load_level === 'overload' ? 'animate-pulse' : ''}`} />
                      <span className={`flex-1 truncate text-[11px] ${
                        m.load_level === 'overload' ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
                      }`}>{m.user_name}</span>
                      <span className="text-[10px] text-[var(--text-secondary)] shrink-0">{m.tasks_active} tarefas</span>
                      {m.tasks_overdue > 0 && (
                        <span className="text-[9px] font-bold text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded-[3px] shrink-0">{m.tasks_overdue} atr.</span>
                      )}
                      <span className={`text-[9px] font-semibold shrink-0 ${LOAD_TEXT[m.load_level]}`}>
                        {LOAD_LABELS[m.load_level]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            )}

          </div>
        </div>
        )} {/* end activeTab === 'live' */}

        {/* ── Heatmap Tab ──────────────────────────────── */}
        {activeTab === 'heatmap' && organizationId && (
          <div className="px-5 pb-5 pt-3 flex flex-col gap-3">
            {/* Capacity controls */}
            <div className={`flex flex-col gap-2 rounded-[4px] border px-4 py-3 transition-colors ${
              heatmapGapMode
                ? 'border-[var(--nav-active-bar)]/40 bg-[var(--nav-active-bar)]/5'
                : 'border-[var(--border-default)] bg-[var(--bg-surface)]'
            }`}>
              {/* Top row: toggle + formula + capacity result */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] shrink-0">Análise de Capacidade</span>
                <button
                  type="button"
                  title="Compara demanda com capacidade disponível (FTE, turno e produtividade)"
                  onClick={() => setHeatmapGapMode(v => !v)}
                  className={`flex items-center gap-1.5 rounded-[3px] border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                    heatmapGapMode
                      ? 'border-[var(--nav-active-bar)] bg-[var(--nav-active-bar)]/15 text-[var(--nav-active-bar)]'
                      : 'border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:border-[var(--nav-active-bar)]/50 hover:text-[var(--nav-active-bar)]'
                  }`}
                >
                  {heatmapGapMode ? '✓ Capacidade ON' : 'Analisar capacidade'}
                </button>

                {!heatmapGapMode && (
                  <span className="text-[9px] text-[var(--text-muted)]">
                    Ative para comparar demanda real vs. capacidade da equipe
                  </span>
                )}

                {heatmapGapMode && (
                  <>
                    <div className="h-3 w-px bg-[var(--border-default)]" />
                    <label className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)]">
                      FTE
                      <input
                        type="number" min={1} max={50} step={1}
                        value={heatmapFte}
                        onChange={e => setHeatmapFte(Math.max(1, Number(e.target.value)))}
                        className="w-12 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] text-center text-[var(--text-primary)]"
                      />
                    </label>
                    <label className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)]">
                      Turno (h)
                      <input
                        type="number" min={4} max={12} step={1}
                        value={heatmapShiftHours}
                        onChange={e => setHeatmapShiftHours(Math.max(4, Number(e.target.value)))}
                        className="w-12 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] text-center text-[var(--text-primary)]"
                      />
                    </label>
                    <label className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)]">
                      Tarefas/FTE/h
                      <input
                        type="number" min={0.1} max={10} step={0.1}
                        value={heatmapTasksPerPersonHour}
                        onChange={e => setHeatmapTasksPerPersonHour(Math.max(0.1, Number(e.target.value)))}
                        className="w-14 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] text-center text-[var(--text-primary)]"
                      />
                    </label>
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-[9px] text-[var(--text-muted)]">
                        Cap. = {heatmapFte} FTE × {heatmapShiftHours}h × {heatmapTasksPerPersonHour} tar/h
                      </span>
                      <span className="rounded-[3px] border border-[var(--nav-active-bar)]/40 bg-[var(--nav-active-bar)]/10 px-2 py-0.5 text-[10px] font-bold text-[var(--nav-active-bar)]">
                        = {(heatmapFte * heatmapTasksPerPersonHour).toFixed(1)} tar/h
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* When active: clarify what changes visually */}
              {heatmapGapMode && (
                <div className="flex items-center gap-2 text-[9px] text-[var(--text-muted)]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--nav-active-bar)]" />
                  Células mostram % de ocupação · cores baseadas em carga vs. capacidade · gargalos em vermelho
                </div>
              )}
            </div>
            <OperationalHeatmap
              organizationId={organizationId}
              startDate={benchmarkStartDate}
              endDate={benchmarkEndDate}
              capacityConfig={activeCapacityConfig}
              simulationMultiplier={simMode ? simDemandSlider / 100 : undefined}
            />
          </div>
        )}

        {/* ── Benchmark Tab ────────────────────────────── */}
        {activeTab === 'benchmark' && organizationId && (
          <div className="px-5 pb-5 pt-3">
            <BenchmarkTable
              organizationId={organizationId}
              startDate={benchmarkStartDate}
              endDate={benchmarkEndDate}
              minSampleSize={1}
            />
          </div>
        )}

        </div>{/* end scrollable body */}
      </div>

      <ToastContainer />
    </ModuleLayout>
  );
};

// ─── Sub-components ─────────────────────────────────────────

function EmptyPanel({ label, cta, onClick, stable }: { label: string; cta?: string; onClick?: () => void; stable?: boolean }) {
  const isStable = stable || label.startsWith('✔');
  const isWarn = !isStable && label.startsWith('⚠');
  return (
    <div className="flex flex-col items-center justify-center py-3 gap-1.5">
      <span className={`text-[10px] ${
        isStable ? 'text-emerald-500 font-medium'
        : isWarn  ? 'text-amber-500'
        : 'text-[var(--text-secondary)]'
      }`}>{label}</span>
      {cta && onClick && (
        <button
          type="button"
          onClick={onClick}
          className="text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {cta} →
        </button>
      )}
    </div>
  );
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatTimeOpen(createdAt: string): string {
  const diffMin = Math.round((Date.now() - new Date(createdAt).getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
